import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { exploreApi } from '../../api/exploreApi';
import { discoveryLocationApi } from '../../api/discoveryLocationApi';
import { useAuth } from '../../auth/AuthContext';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import ExploreGuestGate from './ExploreGuestGate';
import SocialDiscoveryCard from './SocialDiscoveryCard';
import SocialLocationSelector from '../social-compose/SocialLocationSelector';

import { DISCOVERY_KINDS, normalizeDiscoveryPage } from './exploreModel';
import {
  createCameraRequestForLocation,
  createPostsSelection,
  discoveryKindLabelKey,
  isMapCapableItem,
  reconcileSelectionWithItems,
  selectionFromListItem,
  selectionFromMapGroup,
  selectionFromPost,
} from './explorePostsSelection';
import { SOCIAL_MAP_DISCOVERY_LIMIT, toSocialMapMarker } from './socialMapModel';
import {
  focusedLocationSnapshot,
  normalizeSocialMapTimeRange,
  SOCIAL_MAP_TIME_RANGES,
  socialMapTimeRangeLabel,
} from './socialMapViewportModel';
import { snowflakePositiveId } from '../social-compose/socialComposeModel';

const SocialMapSurface = lazy(() => import('./SocialMapSurface'));

/** 單一已套用篩選狀態；變更即套用，沒有套用／取消步驟。 */
const DEFAULT_FILTERS = Object.freeze({ kind: 'ALL', timeRange: 'ALL' });

/** view=map → map; view=list or missing → list (legacy deep links without view stay list). */
function readView(searchParams) {
  return searchParams.get('view') === 'map' ? 'map' : 'list';
}

/**
 * Posts (social) explore panel。
 * list：GET /social/discovery/posts 分頁；map：marker 來自可視範圍地點聚合（SocialMapSurface），不呼叫全域貼文清單。
 * @param {{ returnTo?: string, forceView?: 'list'|'map'|null, mapMode?: boolean }} props
 * forceView／mapMode：IA v2「地圖」tab 強制 map shell，隱藏 list|map 雙切換。
 */
export default function SocialExplorePanel({
  returnTo = '/explore?tab=social',
  forceView = null,
  mapMode = false,
}) {
  const { isAuthenticated } = useAuth();
  const { t } = useTravelTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = readView(searchParams);
  const view = forceView === 'map' || forceView === 'list' ? forceView : urlView;
  const locationId = snowflakePositiveId(searchParams.get('locationId'));
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { kind, timeRange } = filters;
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selection, setSelection] = useState(() => createPostsSelection());
  const [cameraRequest, setCameraRequest] = useState(null);
  const [focusedLocation, setFocusedLocation] = useState(null);
  const generation = useRef(0);
  const listFetchCount = useRef(0);
  const cameraNonce = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;
  const appliedFilters = useRef(filters);

  const locationFilter = useMemo(() => (
    locationId ? { locationId, locationScope: 'DESCENDANTS' } : {}
  ), [locationId]);

  function requestCamera(location, source, postId = null) {
    cameraNonce.current += 1;
    const next = createCameraRequestForLocation(location, {
      nonce: cameraNonce.current,
      source,
      postId,
    });
    if (next) setCameraRequest(next);
  }

  const load = useCallback(async ({ append = false, cursor = null } = {}) => {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      listFetchCount.current += 1;
      const payload = await exploreApi.listPosts({
        kind,
        cursor: cursor || undefined,
        limit: SOCIAL_MAP_DISCOVERY_LIMIT,
        ...locationFilter,
      });
      if (requestGeneration !== generation.current) return;
      const page = normalizeDiscoveryPage(payload);
      setItems((current) => (append ? [...current, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setError(resolveSafeUserVisibleError(cause, t, 'travel.discover.error.postsLoadFailed'));
      setPhase('error');
    }
  }, [kind, locationFilter, t]);

  useEffect(() => {
    if (forceView !== 'map') return;
    if (searchParams.get('view') === 'map') return;
    const params = new URLSearchParams(searchParams);
    params.set('view', 'map');
    setSearchParams(params, { replace: true });
  }, [forceView, searchParams, setSearchParams]);

  const resetListResults = useCallback(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setError('');
  }, []);

  useEffect(() => {
    resetListResults();
    setSelection(createPostsSelection());
    setFocusedLocation(null);
    setCameraRequest(null);
    if (!isAuthenticated) setPhase('idle');
  }, [isAuthenticated, resetListResults]);

  // 篩選變更：清單結果一律作廢；地圖模式保留聚焦地點與選取（不移動 camera、不重設 sheet），
  // 聚焦快照的數字屬於舊篩選結果，改為不顯示，待新回應提供 backend 數字。
  useEffect(() => {
    const previous = appliedFilters.current;
    if (previous === filters) return;
    appliedFilters.current = filters;
    if (previous.kind !== filters.kind) resetListResults();
    if (viewRef.current === 'map') {
      setFocusedLocation((current) => (current ? { ...current, count: null, countLabel: null } : current));
      return;
    }
    if (previous.kind !== filters.kind) {
      setSelection(createPostsSelection());
      setFocusedLocation(null);
      setCameraRequest(null);
    }
  }, [filters, resetListResults]);

  // 地圖不使用全域貼文清單當 marker 來源；只有清單模式才載入。
  useEffect(() => {
    if (isAuthenticated && view === 'list') load();
  }, [isAuthenticated, view, load]);

  useEffect(() => {
    if (view !== 'list') return;
    setSelection((current) => reconcileSelectionWithItems(current, items));
  }, [items, view]);

  useEffect(() => {
    if (!locationId) {
      setSelectedLocation(null);
      return;
    }
    if (selectedLocation?.locationId === locationId) return;
    let cancelled = false;
    discoveryLocationApi.getById(locationId).then((payload) => {
      if (cancelled) return;
      const node = payload?.locationId ? payload : payload?.data;
      if (node?.locationId) {
        setSelectedLocation({
          locationId: String(node.locationId),
          displayName: node.displayName || node.canonicalName || 'Location',
          hasChildren: Boolean(node.hasChildren),
        });
      }
    }).catch(() => {
      if (!cancelled) {
        setSelectedLocation({
          locationId,
          displayName: t('travel.discover.filters.selectedLocation'),
          hasChildren: false,
        });
      }
    });
    return () => { cancelled = true; };
  }, [locationId, selectedLocation?.locationId, t]);

  function patchParams(next) {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (value == null || value === '') params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params, { replace: true });
  }

  function selectKind(nextKind) {
    if (nextKind !== kind) setFilters((current) => ({ ...current, kind: nextKind }));
  }

  function selectTimeRange(nextTimeRange) {
    const value = normalizeSocialMapTimeRange(nextTimeRange);
    if (value !== timeRange) setFilters((current) => ({ ...current, timeRange: value }));
  }

  function clearAllFilters() {
    if (kind !== 'ALL' || timeRange !== 'ALL') setFilters(DEFAULT_FILTERS);
  }

  function selectView(nextView) {
    patchParams({ view: nextView === 'map' ? 'map' : 'list' });
  }

  function onSelectLocation(node) {
    const id = snowflakePositiveId(node?.locationId);
    setSelectedLocation(node);
    patchParams({ locationId: id });
  }

  /** location：地圖提供的聚焦地點快照（與 marker 集合分開保存）。 */
  function onSelectGroup(groupKey, source = 'marker', location = null) {
    const snapshot = location?.locationId === groupKey
      ? location
      : (focusedLocation?.locationId === groupKey ? focusedLocation : null);
    setSelection(selectionFromMapGroup(groupKey));
    setFocusedLocation(snapshot);
    requestCamera(snapshot, source);
  }

  /** sourceItem：聚焦地點的 backend 排序結果；貼文必須屬於該地點才成立深聚焦。 */
  function onSelectPost(postId, groupKey, sourceItem = null) {
    const candidates = sourceItem ? [sourceItem, ...items] : items;
    const next = selectionFromPost(candidates, groupKey, postId);
    setSelection(next);
    const snapshot = focusedLocation?.locationId === next.groupKey
      ? focusedLocation
      : focusedLocationSnapshot(toSocialMapMarker(sourceItem));
    if (snapshot && snapshot.locationId !== focusedLocation?.locationId) setFocusedLocation(snapshot);
    if (next.groupKey) requestCamera(snapshot, 'post', next.postId);
  }

  function onShowOnMap(item) {
    if (!isMapCapableItem(item)) return;
    const next = selectionFromListItem(item);
    const snapshot = focusedLocationSnapshot(toSocialMapMarker(item));
    setSelection(next);
    setFocusedLocation(snapshot);
    if (next.groupKey) requestCamera(snapshot, 'list', next.postId);
    patchParams({ view: 'map' });
  }

  function updateEngagement(postId, property, active) {
    const count = property === 'liked' ? 'likeCount' : 'saveCount';
    setItems((current) => current.map((row) => (String(row.postId) !== String(postId) ? row : ({
      ...row,
      engagement: {
        ...row.engagement,
        [property]: active,
        [count]: Math.max(0, row.engagement[count] + (active ? 1 : -1)),
      },
    }))));
  }

  function handleHide(postId) {
    setItems((current) => current.filter((row) => String(row.postId) !== String(postId)));
  }

  function handleNotInterested(postId) {
    setItems((current) => current.filter((row) => String(row.postId) !== String(postId)));
  }

  function handleMute(creatorMemberId) {
    setItems((current) => current.filter((row) => String(row.author?.memberId) !== String(creatorMemberId)));
  }

  if (!isAuthenticated) {
    // 地圖／貼文探索仍依既有契約需登入；不繞過權限，只改善引導
    return (
      <ExploreGuestGate
        testId="social-guest-wall"
        titleId="explore-login-title"
        titleKey={mapMode ? 'travel.discoverGuest.mapTitle' : 'travel.discoverGuest.socialTitle'}
        bodyKey={mapMode ? 'travel.discoverGuest.mapBody' : 'travel.discoverGuest.socialBody'}
      />
    );
  }

  return (
    <div
      className={`explore-posts${mapMode ? ' explore-posts--map-mode' : ''}`}
      data-testid="explore-posts"
      data-discovery-fetches={listFetchCount.current}
      data-map-mode={mapMode ? 'true' : undefined}
    >
      {!mapMode ? (
        <div
          className="explore-view-switch explore-view-switch--primary"
          role="tablist"
          aria-label={t('travel.discover.posts.viewLabel')}
          data-testid="posts-view-switch"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === 'list'}
            data-testid="posts-view-list"
            onClick={() => selectView('list')}
          >
            {t('travel.discover.posts.list')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'map'}
            data-testid="posts-view-map"
            onClick={() => selectView('map')}
          >
            {t('travel.discover.posts.map')}
          </button>
        </div>
      ) : null}

      <section className="explore-posts-filters" aria-label={t('travel.discover.posts.filtersLabel')} data-testid="posts-filters">
        {view === 'list' ? (
          <div className="explore-posts-filter explore-posts-filter--location" data-testid="posts-location-filter">
            <h2 className="explore-posts-filter__title">{t('travel.discover.posts.location')}</h2>
            <SocialLocationSelector
              selected={selectedLocation}
              onSelect={onSelectLocation}
              rootLabel={t('travel.discover.posts.allLocations')}
            />
          </div>
        ) : null}
        <div className="explore-posts-filter explore-posts-filter--kind" data-testid="posts-kind-filter">
          <h2 className="explore-posts-filter__title">{t('travel.discover.posts.kind')}</h2>
          <div className="explore-filters" role="tablist" aria-label={t('travel.discover.posts.kind')}>
            {DISCOVERY_KINDS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={kind === value}
                data-testid={`posts-kind-${value}`}
                onClick={() => selectKind(value)}
              >
                {t(discoveryKindLabelKey(value))}
              </button>
            ))}
          </div>
          {view === 'map' ? (
            <div className="explore-time-filter" data-testid="posts-time-filter">
              <label className="explore-time-filter__label" htmlFor="posts-time-range">發布時間</label>
              <select
                id="posts-time-range"
                className={`explore-time-filter__select${timeRange !== 'ALL' ? ' is-active' : ''}`}
                value={timeRange}
                data-active={timeRange !== 'ALL' ? 'true' : 'false'}
                data-testid="posts-time-range"
                onChange={(event) => selectTimeRange(event.target.value)}
              >
                {SOCIAL_MAP_TIME_RANGES.map((value) => (
                  <option key={value} value={value}>{socialMapTimeRangeLabel(value)}</option>
                ))}
              </select>
              {timeRange !== 'ALL' ? (
                <button
                  type="button"
                  className="explore-time-filter__clear"
                  data-testid="posts-time-clear"
                  onClick={() => selectTimeRange('ALL')}
                >
                  清除時間
                </button>
              ) : null}
              {kind !== 'ALL' || timeRange !== 'ALL' ? (
                <button
                  type="button"
                  className="explore-time-filter__clear"
                  data-testid="posts-filters-clear-all"
                  onClick={clearAllFilters}
                >
                  清除全部篩選
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {!mapMode ? (
        <p className="explore-posts-secondary" data-testid="posts-following-entry">
          <Link to="/explore?tab=following" className="explore-posts-secondary__link">
            {t('travel.discover.posts.following')}
          </Link>
          <span className="explore-posts-secondary__hint">{t('travel.discover.posts.followingHint')}</span>
        </p>
      ) : null}

      {view === 'list' && (phase === 'loading' || phase === 'idle') ? (
        <section className="explore-state" aria-live="polite"><div className="explore-spinner" /><p>{t('travel.discover.loading')}</p></section>
      ) : null}
      {view === 'list' && phase === 'error' && items.length === 0 ? (
        <section className="explore-state explore-state--error" role="alert">
          <h2>{t('travel.discover.error.title')}</h2><p>{error}</p>
          <button type="button" onClick={() => load()}>{t('travel.common.retry')}</button>
        </section>
      ) : null}
      {view === 'list' ? (
        <>
          {phase === 'ready' && items.length === 0 ? (
            <section className="explore-state"><h2>{t('travel.discover.empty.title')}</h2><p>{t('travel.discover.empty.hint')}</p></section>
          ) : null}
          <div className="explore-cards">
            {items.map((item) => (
              <SocialDiscoveryCard
                key={item.postId}
                item={item}
                returnTo={returnTo}
                onEngagementChange={updateEngagement}
                onHide={handleHide}
                onNotInterested={handleNotInterested}
                onMute={handleMute}
                onShowOnMap={onShowOnMap}
              />
            ))}
          </div>
          {error && items.length > 0 ? (
            <div className="explore-inline-error" role="alert">
              {error} <button type="button" onClick={() => load({ append: true, cursor: nextCursor })}>{t('travel.common.retry')}</button>
            </div>
          ) : null}
          {hasMore ? (
            <button className="explore-load-more" type="button" disabled={phase === 'loading-more'}
              onClick={() => load({ append: true, cursor: nextCursor })}>
              {phase === 'loading-more' ? t('travel.discover.loading') : t('travel.common.loadMore')}
            </button>
          ) : null}
          {items.length > 0 && !hasMore && phase === 'ready' ? (
            <p className="explore-end">{t('travel.discover.end')}</p>
          ) : null}
        </>
      ) : (
        <Suspense fallback={<section className="explore-state" aria-live="polite"><div className="explore-spinner" /><p>{t('travel.discover.loading')}</p></section>}>
          <SocialMapSurface
            kind={kind}
            timeRange={timeRange}
            focusedLocation={focusedLocation}
            selectedKey={selection.groupKey}
            selectedPostId={selection.postId}
            cameraRequest={cameraRequest}
            onSelectGroup={onSelectGroup}
            onSelectPost={onSelectPost}
            returnTo={returnTo}
            returnSearch={searchParams.toString() ? `?${searchParams.toString()}` : ''}
          />
        </Suspense>
      )}
    </div>
  );
}
